import { Injectable } from '@angular/core';
import { DataService } from './http/data.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { registerPlugin } from '@capacitor/core';
import type { BadgePlugin } from './definitions';

// Definir el plugin de Badge
const Badge = registerPlugin<BadgePlugin>('Badge', {
  web: () => import('./badge.web').then(m => new m.BadgeWeb()),
});

export interface Notification {
  id_notification: number;
  id_campaign?: number;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'commission';
  is_read: boolean;
  read_at?: string;
  action_url?: string;
  metadata?: any;
  created_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private pendingToken: string | null = null; // Token pendiente de registro
  private tokenRegistered: boolean = false; // Flag para saber si el token ya fue registrado

  constructor(
    private dataService: DataService
  ) {
    // Inicializar push notifications de forma asíncrona para evitar errores en el constructor
    setTimeout(() => {
      this.initializePushNotifications().catch(err => {
        console.error('Error inicializando push notifications:', err);
      });
    }, 0);
    
    // Escuchar el evento personalizado de FCM token desde iOS
    if (Capacitor.isNativePlatform()) {
      window.addEventListener('fcmTokenReceived', ((event: CustomEvent) => {
        console.log('🔥🔥🔥 Token recibido desde AppDelegate vía evento:', event.detail);
        try {
          const data = typeof event.detail === 'string' ? JSON.parse(event.detail) : event.detail;
          if (data && data.token) {
            this.pendingToken = data.token;
            console.log('✅ Token guardado en pendingToken desde evento. Intentando registrar...');
            this.registerDeviceToken(data.token).catch(err => {
              console.error('Error registrando token desde evento:', err);
            });
          }
        } catch (e) {
          console.error('Error procesando evento fcmTokenReceived:', e);
        }
      }) as EventListener);
    }
    
    // Suscribirse a cambios en el conteo de no leídas para actualizar el badge
    this.unreadCount$.subscribe(count => {
      this.updateBadge(count);
    });
  }

  /**
   * Inicializa las push notifications usando Capacitor
   */
  async initializePushNotifications() {
    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications solo disponibles en plataformas nativas');
      return;
    }

    try {
      // Solicitar permisos
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
      
      if (permStatus.receive !== 'granted') {
        console.log('Permisos de notificaciones denegados');
        return;
      }

      // Registrar para recibir notificaciones
      console.log('📱 Registrando para recibir notificaciones push...');
      await PushNotifications.register();
      console.log('✅ PushNotifications.register() completado. Esperando token...');

      // Escuchar cuando se recibe una notificación
      PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
        console.log('Push notification received:', notification);
        // Recargar notificaciones cuando se recibe una nueva (esto actualizará el badge automáticamente)
        this.loadNotifications();
      });

      // Escuchar cuando se hace clic en una notificación
      PushNotifications.addListener('pushNotificationActionPerformed', (action: any) => {
        console.log('Push notification action performed:', action);
        const data = action.notification.data;
        if (data && data.notification_id) {
          this.markAsRead(parseInt(data.notification_id));
        }
        // Recargar notificaciones
        this.loadNotifications();
      });

      // Obtener el token y registrarlo
      PushNotifications.addListener('registration', async (token: any) => {
        console.log('🔥🔥🔥 Push registration token recibido:', token.value);
        console.log('Token completo:', JSON.stringify(token));
        // Guardar el token (puede que el usuario aún no esté logueado)
        this.pendingToken = token.value;
        console.log('✅ Token guardado en pendingToken. Intentando registrar...');
        // Intentar registrarlo si hay usuario, si no, se registrará después del login
        await this.registerDeviceToken(token.value);
      });

      PushNotifications.addListener('registrationError', (error: any) => {
        console.error('❌❌❌ Error en registro de push notifications:', error);
        console.error('Detalles del error:', JSON.stringify(error));
      });
      
      console.log('👂 Listeners de push notifications configurados');

    } catch (error) {
      console.error('Error inicializando push notifications:', error);
    }
  }

  /**
   * Registra el token del dispositivo en el backend
   */
  async registerDeviceToken(token?: string) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id_user) {
        console.log('Usuario no encontrado para registrar token. Se registrará después del login.');
        // Guardar el token para registrarlo después del login
        if (token) {
          this.pendingToken = token;
        }
        return;
      }

      // Usar el token pendiente si no se proporciona uno
      const tokenToRegister = token || this.pendingToken;
      if (!tokenToRegister) {
        console.log('No hay token disponible para registrar');
        return;
      }

      const platform = Capacitor.getPlatform();
      const deviceId = await this.getDeviceId();

      console.log('Enviando registro de token al backend:', {
        id_user: user.id_user,
        token: tokenToRegister.substring(0, 20) + '...',
        platform: platform,
        device_id: deviceId
      });

      const response = await this.dataService.RegisterDeviceToken({
        id_user: user.id_user,
        token: tokenToRegister,
        platform: platform,
        device_id: deviceId
      });

      console.log('✅ Token registrado exitosamente en el backend:', response);
      // Limpiar el token pendiente después de registrarlo exitosamente
      this.pendingToken = null;
      this.tokenRegistered = true;
    } catch (error) {
      console.error('Error registrando token:', error);
    }
  }

  /**
   * Método público para registrar el token después del login
   * Debe ser llamado después de que el usuario inicie sesión
   */
  async registerTokenAfterLogin() {
    // IMPORTANTE: Este método NO debe lanzar errores que bloqueen el login
    // Todos los errores deben ser manejados internamente
    
    // Ejecutar de forma completamente asíncrona para no bloquear
    return new Promise<void>((resolve) => {
      setTimeout(async () => {
        // Envolver TODO en un try-catch INMEDIATAMENTE para capturar cualquier error
        // incluso antes de los logs
        try {
          // Verificar permisos nuevamente después del login (por si el usuario los habilitó)
          if (Capacitor.isNativePlatform()) {
            try {
              let permStatus = await PushNotifications.checkPermissions();
              console.log('🔔 Estado de permisos después del login:', permStatus);
              
              if (permStatus.receive === 'prompt') {
                console.log('🔔 Solicitando permisos nuevamente...');
                permStatus = await PushNotifications.requestPermissions();
                console.log('🔔 Resultado de solicitud de permisos:', permStatus);
              }
              
              if (permStatus.receive === 'granted') {
                console.log('✅ Permisos de notificaciones habilitados');
                // Registrar para recibir notificaciones si aún no está registrado
                try {
                  await PushNotifications.register();
                  console.log('✅ PushNotifications.register() completado después del login');
                } catch (regError) {
                  console.log('⚠️ Error en register (puede que ya esté registrado):', regError);
                }
              } else {
                console.log('⚠️ Permisos de notificaciones aún no están habilitados:', permStatus.receive);
                console.log('💡 El usuario debe habilitarlos en Configuración → [App] → Notificaciones');
              }
            } catch (permError) {
              console.log('⚠️ Error verificando permisos (no crítico):', permError);
            }
          }
          
          // Log inmediato DESPUÉS del try para asegurar que estamos dentro del try-catch
          console.log('🔔 [INICIO] registerTokenAfterLogin llamado. pendingToken:', this.pendingToken, 'tokenRegistered:', this.tokenRegistered);
      
      // Si el token ya fue registrado, no hacer nada
      if (this.tokenRegistered) {
        console.log('✅ Token ya fue registrado anteriormente. No es necesario registrarlo de nuevo.');
        return;
      }
      
      // Si ya tenemos el token, registrarlo inmediatamente
      if (this.pendingToken) {
        console.log('✅ Token pendiente encontrado. Registrando...');
        await this.registerDeviceToken(this.pendingToken);
        return;
      }
      
      // Intentar obtener el token directamente desde el plugin de Firebase
      // Esto es un fallback cuando Capacitor PushNotifications no lo proporciona
      try {
        const isNative = Capacitor.isNativePlatform();
        console.log('🔍 Verificando plataforma nativa:', isNative);
        
        if (isNative) {
          try {
            console.log('🔍 Intentando obtener token desde FirebaseToken plugin...');
            // Acceder al plugin directamente a través de Capacitor
            // En Capacitor, los plugins se acceden a través de Plugins.[NombrePlugin]
            const Plugins = (Capacitor as any).Plugins;
            console.log('🔍 Plugins object disponible:', !!Plugins);
            
            if (Plugins) {
              console.log('🔍 Plugins disponibles:', Object.keys(Plugins));
              const FirebaseToken = Plugins.FirebaseToken;
              console.log('🔍 FirebaseToken plugin disponible:', !!FirebaseToken);
              
              if (FirebaseToken && typeof FirebaseToken.getToken === 'function') {
                console.log('✅ FirebaseToken plugin encontrado, llamando getToken...');
                const result = await FirebaseToken.getToken();
                if (result && result.token) {
                  console.log('✅ Token obtenido desde FirebaseToken plugin:', result.token.substring(0, 20) + '...');
                  this.pendingToken = result.token;
                  await this.registerDeviceToken(result.token);
                  return;
                } else {
                  console.log('⚠️ Plugin respondió pero no hay token en la respuesta');
                }
              } else {
                console.log('⚠️ FirebaseToken plugin no está disponible o no está registrado');
                console.log('💡 El plugin debe estar en ios/App/App/Plugins/FirebaseTokenPlugin.swift');
              }
            } else {
              console.log('⚠️ Plugins object no está disponible en Capacitor');
            }
          } catch (pluginError: any) {
            console.log('⚠️ No se pudo obtener token desde plugin (puede que no esté disponible):', pluginError?.message || pluginError);
            console.log('💡 Error completo:', JSON.stringify(pluginError));
            // Continuar con el método alternativo
          }
        } else {
          console.log('⚠️ No es plataforma nativa, saltando plugin de Firebase');
        }
      } catch (capacitorError: any) {
        console.log('⚠️ Error accediendo a Capacitor:', capacitorError?.message || capacitorError);
        console.log('💡 Error completo:', JSON.stringify(capacitorError));
        // Continuar con el método alternativo
      }
      
      // Esperar a que Capacitor reciba el token a través del listener 'registration'
      // El listener ya está configurado en initializePushNotifications()
      console.log('⚠️ No hay token pendiente. Esperando token de Capacitor...');
      console.log('💡 El token se registrará automáticamente cuando llegue el evento registration.');
      console.log('💡 Nota: El token de Firebase está disponible en UserDefaults pero Capacitor lo recibirá pronto.');
      
      // Intentar esperar un poco para ver si el token llega
      for (let i = 0; i < 3; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Verificar si el token llegó mientras esperábamos
        if (this.pendingToken) {
          console.log(`✅ Token recibido después de ${i + 1} segundo(s). Registrando...`);
          await this.registerDeviceToken(this.pendingToken);
          return;
        }
        
        console.log(`⏳ Esperando token... Intento ${i + 1}/3`);
      }
      
          console.log('⚠️ Token no recibido después de 3 segundos.');
          console.log('💡 El token se registrará automáticamente cuando llegue el evento registration de Capacitor.');
          console.log('💡 El login continuará normalmente.');
          
        } catch (error: any) {
          // NO re-lanzar el error - el login no debe fallar por problemas con el token
          console.error('❌ [ERROR] Error en registerTokenAfterLogin (no crítico):', error?.message || error);
          console.error('💡 Código de error:', error?.code);
          console.log('💡 El login continuará normalmente. El token se registrará cuando esté disponible.');
          // NO hacer throw - el login debe continuar incluso si el token no se puede registrar
        } finally {
          resolve();
        }
      }, 0);
    });
  }

  /**
   * Intenta forzar el registro del token
   * NOTA: Este método puede fallar con error -203 si Capacitor no puede obtener el token
   */
  private async forceTokenRegistration() {
    try {
      // NO intentar PushNotifications.register() porque falla con error -203
      // En su lugar, solo esperar a que el token llegue desde el listener
      console.log('📱 Esperando token desde el listener de Capacitor...');
      
      // Esperar un poco más para que el token llegue
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (this.pendingToken) {
        console.log('✅ Token encontrado en pendingToken. Registrando...');
        await this.registerDeviceToken(this.pendingToken);
      } else {
        console.log('⚠️ Aún no hay token. El token se registrará cuando llegue el evento registration.');
        console.log('💡 Nota: El token de Firebase está disponible en UserDefaults pero Capacitor no lo ha recibido aún.');
      }
    } catch (error) {
      console.error('❌ Error forzando registro:', error);
    }
  }

  /**
   * Obtiene un ID único del dispositivo
   */
  private async getDeviceId(): Promise<string> {
    // En una implementación real, podrías usar @capacitor/device
    // Por ahora, usamos un ID basado en localStorage
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  }

  /**
   * Carga las notificaciones del usuario
   */
  async loadNotifications(unreadOnly: boolean = false) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id_user) {
        return;
      }

      const response: any = await this.dataService.GetNotifications({
        id_user: user.id_user,
        limit: 100,
        offset: 0,
        unread_only: unreadOnly
      });

      if (response.status === '000' && response.data) {
        this.notificationsSubject.next(response.data.notifications || []);
        const unreadCount = response.data.unread_count || 0;
        console.log(`🔔 loadNotifications: Conteo de no leídas recibido del backend: ${unreadCount}`);
        this.unreadCountSubject.next(unreadCount);
        // Actualizar badge explícitamente después de cargar notificaciones
        // Esto asegura que se actualice incluso si la suscripción no se ejecuta
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
          this.updateBadge(unreadCount);
        }
      }
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
    }
  }

  /**
   * Marca una notificación como leída
   */
  async markAsRead(idNotification: number) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id_user) {
        return;
      }

      await this.dataService.MarkNotificationRead({
        id_user: user.id_user,
        id_notification: idNotification
      });

      // Recargar notificaciones
      await this.loadNotifications();
    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
    }
  }

  /**
   * Marca todas las notificaciones como leídas
   */
  async markAllAsRead() {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id_user) {
        return;
      }

      await this.dataService.MarkNotificationRead({
        id_user: user.id_user,
        mark_all: true
      });

      // Recargar notificaciones
      await this.loadNotifications();
    } catch (error) {
      console.error('Error marcando todas las notificaciones como leídas:', error);
    }
  }

  /**
   * Borra una notificación específica
   */
  async deleteNotification(idNotification: number) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id_user) {
        return;
      }

      await this.dataService.DeleteNotification({
        id_user: user.id_user,
        id_notification: idNotification
      });

      // Recargar notificaciones
      await this.loadNotifications();
    } catch (error) {
      console.error('Error borrando notificación:', error);
      throw error;
    }
  }

  /**
   * Borra todas las notificaciones del usuario
   */
  async deleteAllNotifications() {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id_user) {
        return;
      }

      await this.dataService.DeleteNotification({
        id_user: user.id_user,
        delete_all: true
      });

      // Recargar notificaciones
      await this.loadNotifications();
    } catch (error) {
      console.error('Error borrando todas las notificaciones:', error);
      throw error;
    }
  }

  /**
   * Obtiene el conteo de notificaciones no leídas
   */
  getUnreadCount(): Observable<number> {
    return this.unreadCount$;
  }

  /**
   * Obtiene las notificaciones
   */
  getNotifications(): Observable<Notification[]> {
    return this.notifications$;
  }

  /**
   * Actualiza el badge del icono de la app en iOS
   */
  private async updateBadge(count: number) {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      console.log(`🔔 updateBadge: No es iOS, saltando actualización. Plataforma: ${Capacitor.getPlatform()}`);
      return;
    }

    try {
      console.log(`🔔 updateBadge: Intentando actualizar badge a ${count}...`);
      
      // Método 1: Intentar usar el plugin Badge si está disponible
      if (Badge && typeof Badge.setBadge === 'function') {
        try {
          const result = await Badge.setBadge({ count });
          console.log(`✅ Badge actualizado a ${count} usando plugin. Resultado:`, result);
          return;
        } catch (pluginError: any) {
          console.warn('⚠️ Error usando plugin Badge, usando método alternativo:', pluginError?.message || pluginError);
        }
      }
      
      // Método 2: Usar la función global updateAppBadge configurada en AppDelegate
      if (typeof (window as any).updateAppBadge === 'function') {
        try {
          console.log(`🔔 updateBadge: Usando función global updateAppBadge...`);
          (window as any).updateAppBadge(count);
          console.log(`✅ Función updateAppBadge llamada con count: ${count}`);
          return;
        } catch (globalError: any) {
          console.warn('⚠️ Error usando updateAppBadge global:', globalError?.message || globalError);
        }
      }
      
      // Método 3: Disparar evento que AppDelegate escuchará
      console.log(`🔔 updateBadge: Disparando evento updateBadgeNative...`);
      window.dispatchEvent(new CustomEvent('updateBadgeNative', { detail: { count } }));
      
      // También intentar después de un delay por si el plugin se carga más tarde
      setTimeout(async () => {
        if (Badge && typeof Badge.setBadge === 'function') {
          try {
            const result = await Badge.setBadge({ count });
            console.log(`✅ Badge actualizado en segundo intento a ${count}. Resultado:`, result);
          } catch (retryError) {
            console.error('❌ Error en segundo intento:', retryError);
          }
        } else if (typeof (window as any).updateAppBadge === 'function') {
          (window as any).updateAppBadge(count);
        }
      }, 2000);
      
    } catch (error: any) {
      console.error('❌ Error actualizando badge:', error);
      console.error('❌ Error details:', error?.message || JSON.stringify(error));
    }
  }
}



